import React from "react";
import { Box, Typography, Chip, Tooltip } from "@mui/material";
import { formatUnderscoreText } from "../../utils/activityLogUtils";
import { formatDateTimeDMY } from "../../utils/utilityFunctions";

/**
 * Render performer information cell
 */
export const PerformerCell = ({ log }) => {
  if (!log.performer) {
    return <Typography variant="body2" fontSize="0.813rem">—</Typography>;
  }

  const name = `${log.performer.first_name || ""} ${
    log.performer.last_name || ""
  }`.trim();
  const email = log.performer.email || "";

  return (
    <Tooltip title={email} arrow>
      <Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontSize: "0.813rem", lineHeight: 1.3 }}
        >
          {name || "Unknown"}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.7rem" }}
        >
          ID: {log.performer.user_id}
        </Typography>
      </Box>
    </Tooltip>
  );
};

/**
 * Render stage information cell
 */
export const StageCell = ({ log }) => {
  if (!log.stage) {
    return <Typography variant="body2" fontSize="0.813rem">—</Typography>;
  }

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, fontSize: "0.813rem", lineHeight: 1.3 }}
      >
        {log.stage.stage_name || log.stage_name || "—"}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: "0.7rem" }}
      >
        {formatUnderscoreText(log.stage.stage_number || log.stage_number)}
      </Typography>
    </Box>
  );
};

/**
 * Render status change cell with chips
 */
export const StatusChangeCell = ({ log }) => {
  if (!log.previous_status && !log.new_status) return null;

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}
    >
      {log.previous_status && (
        <Chip
          label={formatUnderscoreText(log.previous_status)}
          size="small"
          variant="outlined"
          sx={{ fontSize: "0.7rem", height: "22px" }}
        />
      )}
      {log.previous_status && log.new_status && (
        <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
          →
        </Typography>
      )}
      {log.new_status && (
        <Chip
          label={formatUnderscoreText(log.new_status)}
          size="small"
          color={log.new_status === "Completed" ? "success" : "primary"}
          sx={{ fontSize: "0.7rem", height: "22px" }}
        />
      )}
    </Box>
  );
};

/**
 * Render timestamp cell
 */
export const TimestampCell = ({ log }) => {
  if (!log.action_timestamp) {
    return <Typography variant="body2" fontSize="0.813rem">—</Typography>;
  }

  return (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        whiteSpace: "nowrap",
        fontSize: "0.813rem",
        lineHeight: 1.3,
      }}
    >
      {formatDateTimeDMY(log.action_timestamp)}
    </Typography>
  );
};

/**
 * Render message cell with formatted field names
 * Replaces underscores and dashes with spaces in field names
 */
export const MessageCell = ({ log }) => {
  // Format the message to replace underscores and dashes with spaces
  const formatMessage = (message) => {
    if (!message) return "—";
    
    let formattedMessage = message;
    
    // First, replace field names with underscores (e.g., field_name, Stage_7) with spaces
    // This regex matches words with underscores and formats them
    formattedMessage = formattedMessage.replace(/\b([a-z]+_[a-z0-9_]+)\b/gi, (match) => {
      return formatUnderscoreText(match);
    });
    
    // Then, replace dashes with spaces (e.g., stage-3, primer-coat) with spaces
    // This regex matches words with dashes and formats them
    formattedMessage = formattedMessage.replace(/\b([a-z]+(?:-[a-z0-9]+)+)\b/gi, (match) => {
      // Replace dashes with spaces and capitalize each word
      return match
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    });
    
    return formattedMessage;
  };

  return (
    <Typography
      variant="body2"
      sx={{
        wordBreak: "break-word",
        fontSize: "0.813rem",
        lineHeight: 1.4,
      }}
    >
      {formatMessage(log.message)}
    </Typography>
  );
};
